import React from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AccordionSummaryProps,
  Box,
  Divider,
  DownIcon,
  Stack,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"

// Accordion reads id and aria-controls off its first child to label the panel, so the heading wraps them as props.
const HeadingSummary = ({ component, ...summaryProps }: AccordionSummaryProps & { component: "h2" }) => (
  <Box component={component} sx={{ margin: 0 }}>
    <AccordionSummary {...summaryProps} />
  </Box>
)

export const CategoryAccordion = ({
  id,
  title,
  titleVariant = "h5",
  headingComponent,
  titleAction,
  children
}: React.PropsWithChildren<{
  id: string
  title: string
  titleVariant?: "h2" | "h5"
  headingComponent?: "h2"
  titleAction?: React.ReactNode
}>): JSX.Element => {
  const theme = useTheme()
  const summaryProps = {
    expandIcon: <DownIcon fontSize="large" />,
    "aria-controls": `${id}-content`,
    id: `${id}-header`
  }
  const summaryContent = (
    <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.xxs)}>
      <Typography
        variant={titleVariant}
        {...(headingComponent && { component: "span" })}
        sx={(theme) => ({ fontWeight: "normal", color: theme.palette.text.primary })}
      >
        {title}
      </Typography>
      {titleAction}
    </Stack>
  )
  return (
    <>
      <Box>
        <Accordion>
          {/* A heading inside the summary's role="button" isn't announced as a heading, so it has to wrap the summary. */}
          {headingComponent ? (
            <HeadingSummary component={headingComponent} {...summaryProps}>
              {summaryContent}
            </HeadingSummary>
          ) : (
            <AccordionSummary {...summaryProps}>{summaryContent}</AccordionSummary>
          )}
          <AccordionDetails sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>{children}</AccordionDetails>
        </Accordion>
      </Box>
      <Divider />
    </>
  )
}
